/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import React from 'react';
import { FlexBox, FlexBoxAlignItems, FlexBoxJustifyContent, Icon, IconPropTypes } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { LaunchSummaryDto } from '@/commons/dtos';
import { PersonalizationStatus } from '@/types';

interface PersonalizationStatusIconProps {
    status: PersonalizationStatus;
}

const personalizationStatusIconMap: Record<
    PersonalizationStatus,
    { icon: IconPropTypes['name']; color: React.CSSProperties['color']; backgroundColor: React.CSSProperties['color']; popoverContent?: string }
> = {
    'not-started': {
        icon: 'sap-icon://circle-task',
        color: ThemingParameters.sapBackgroundColor,
        backgroundColor: ThemingParameters.sapIndicationColor_10_Background,
    },
    'in-progress': {
        icon: 'sap-icon://goalseek',
        color: ThemingParameters.sapBackgroundColor,
        backgroundColor: ThemingParameters.sapInformationColor,
    },
    success: {
        icon: 'sap-icon://sys-enter-2',
        color: ThemingParameters.sapBackgroundColor,
        backgroundColor: ThemingParameters.sapSuccessColor,
    },
    failed: {
        icon: 'sap-icon://sys-cancel-2',
        color: ThemingParameters.sapBackgroundColor,
        backgroundColor: ThemingParameters.sapErrorColor,
    },
};

const PersonalizationStatusIcon: React.FC<PersonalizationStatusIconProps> = ({ status }) => {
    const { icon, color, backgroundColor } = personalizationStatusIconMap[status];
    return (
        <FlexBox
            justifyContent={FlexBoxJustifyContent.Center}
            alignItems={FlexBoxAlignItems.Center}
            style={{
                backgroundColor,
                padding: '6px',
                borderRadius: '8px',
            }}
        >
            <Icon name={icon} style={{ color }} />
        </FlexBox>
    );
};

export function mapJobStatusToPersonalizationStatus(jobStatus: LaunchSummaryDto['status']): PersonalizationStatus {
    return (
        (
            {
                NOT_STARTED: 'not-started',
                FAILED: 'failed',
                RUNNING: 'in-progress',
                COMPLETED: 'success',
                COMPLETED_WITH_ERROR: 'success',
            } as const
        )[jobStatus] ?? 'in-progress'
    );
}

export default PersonalizationStatusIcon;
